import {
  Button,
  CustomInput,
  DatePicker,
  Fieldset,
  Legend,
  PatternGuardedInputText,
  PrimaryButton,
  Select,
  SelectOption,
} from '@paytrace/pt-skel/Component'
import React, { useEffect, useState } from 'react'
import {
  ACHMerchantSettings,
  Customer,
  CustomerAchDetails,
  RECUR_PAYMENT_VALUE,
  RecurAttemptMap,
  RecurFormData,
  RecurFrequencyMap,
  RecurPaymentTypes,
  RecurProcessOnWeekendsMap,
} from '../../types'
import { formatDateToYYYYMMDD, parseDateFromMDYYYY } from '../../Helper/util'
import PrintableConsentForm from './PrintableConsentForm'

interface RecurFormProps {
  formData: RecurFormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> | { target: { name: string; value: any } }) => void;
  handleSubmit: (event: React.FormEvent) => void;
  handleDelete: (event: React.FormEvent) => void;
  supportedRecurTranxTypes: SelectOption[];
  supportedStdEntryClasses: SelectOption[];
  customer: Customer;
  handleChangeLogClick?: () => void;
  achMerchantSettings?: ACHMerchantSettings;
  minDate: Date;
  isEditPage?: boolean;
  submitDisabled?: boolean;
  showSurchargeInfo?: boolean;
  showDeleteButton?: boolean;
  isLoading?: boolean;
  customerAchDetails: CustomerAchDetails;
}

const RecurForm: React.FC<RecurFormProps> = ({
  formData,
  handleChange,
  handleSubmit,
  handleDelete,
  handleChangeLogClick,
  supportedRecurTranxTypes,
  supportedStdEntryClasses,
  achMerchantSettings,
  minDate,
  customer,
  isEditPage = false,
  submitDisabled = false,
  showSurchargeInfo = false,
  showDeleteButton = false,
  isLoading = false,
  customerAchDetails,
}) => {
  const startDateForPicker = formData.start_date ? formatDateToYYYYMMDD(parseDateFromMDYYYY(formData.start_date)) : ''
  const nextDateForPicker = formData.next_date ? formatDateToYYYYMMDD(parseDateFromMDYYYY(formData.next_date)) : ''
  const [isPrintableConsentFormOpen, setPrintableConsentFormOpen] = useState(false)
  const minDateForPicker = formatDateToYYYYMMDD(minDate)

  const surchargeConfirmText = 'Merchant has notified the cardholder of the surcharge and has offered alternative payment methods to avoid the surcharge fee. Cardholder has chosen to move forward with current payment type and incur the surcharge. Cardholder is also aware that the surcharge amount could change but will never exceed 3 percent (2 percent in CO).'

  return (
    <>
    <Fieldset>
      <Legend title="Recurring Payment Properties">
        {isEditPage && handleChangeLogClick && (
          <div className="float-right">
            <div>
              <a onClick={handleChangeLogClick} className="btn-split">Change Log</a>
              <a onClick={() => setPrintableConsentFormOpen(true)} className="btn-split">Printable Consent Form</a>
            </div>
          </div>
        )}
      </Legend>

      {showSurchargeInfo && <div className="form-group" style={{ textIndent: '15em' }}>
        <input
          type="checkbox"
          id="add_surcharge"
          name="add_surcharge"
          checked={!!formData.add_surcharge}
          onChange={handleChange}
        />
        <label htmlFor="add_surcharge" style={{ marginLeft: '0.5em' }}>
          Enable Surcharge for this Recurring Payment
        </label>
      </div>}

      <div className="form-group" style={{ textIndent: '15em' }}>
        <input
          type="checkbox"
          id="customer_receipt"
          name="customer_receipt"
          checked={!!formData.customer_receipt}
          onChange={handleChange}
          disabled={formData.add_surcharge}
        />
        <label htmlFor="customer_receipt" style={{ marginLeft: '0.5em' }}>
          Email Receipt to Customer
        </label>
      </div>

      <Select
        key='transaction_type'
        label="Transaction Type"
        options={supportedRecurTranxTypes}
        name="transaction_type"
        value={formData.transaction_type}
        onChange={handleChange}
        hintText='Please select the type of transaction you wish to generate from this recurring payment.'
        required
      />

      <PatternGuardedInputText
        key='amount'
        onChange={handleChange}
        name="amount"
        label="Amount"
        type="PatternGuardedInputText"
        maxLength={17}
        min='0'
        value={formData.amount ?? ''}
        required={true}
        hintText='Provide the amount that you wish to charge the customer each time this recurring payment is processed. Enter the amount as you would on a calculator with a decimal if necessary.'
      />

      {formData.add_surcharge &&
      <PatternGuardedInputText
        key='surcharge_amount'
        onChange={handleChange}
        name="surcharge_amount"
        label="Surcharge Amount"
        type="number"
        step="0.01"
        value={formData.surcharge_amount ?? ''}
        disabled={true}
        hintText='Enter the amount to charge each time (e.g., 10.50).'
      />}

      {formData.add_surcharge && <PatternGuardedInputText
        key='total_amount'
        onChange={handleChange}
        name="total_amount"
        label="Total Amount"
        type="number"
        step="0.01"
        value={formData.total_amount ?? ''}
        disabled={true}
        hintText='Enter the amount to charge each time (e.g., 10.50).'
      />}

      <Select
        key='frequency'
        label="Frequency"
        options={RecurFrequencyMap}
        name="frequency"
        value={formData.frequency}
        onChange={handleChange}
        required
        hintText='Select how frequently you want this payment to be processed.'
      />

      <Select
        key='process_on_weekends'
        label="Process on Weekends"
        options={RecurProcessOnWeekendsMap}
        name="process_on_weekends"
        value={String(formData.process_on_weekends ?? false)}
        onChange={(e) => handleChange({ target: { name: 'process_on_weekends', value: e.target.value === 'true' } })} // Convert back to boolean
        hintText='Specify if the transaction should be processed on a weekend if the processing date falls on a Saturday or Sunday. If you do not want the transaction to process on a weekend, it will process on the next weekday (Monday-Friday).'
      />

      {!isEditPage && (
        <DatePicker
          key='start_date'
          label="Start Date"
          name="start_date"
          minDate={minDateForPicker}
          selectedDate={startDateForPicker}
          setSelectedDate={(date) => handleChange({
            target: {
              name: 'start_date',
              value: date,
            },
          })}
        />
      )}

      {isEditPage && (
        <div style={{ display: 'flex' }}>
          <label className="form-group" style={{ textIndent: '9em', display: 'inline-block' }}>Start Date</label>
          <div style={{ textIndent: '3em' }}>{formData.start_date}</div>
        </div>
      )}

      {isEditPage && (
        <DatePicker
          key='next_date'
          label="Next Date"
          name="next_date"
          minDate={minDateForPicker}
          selectedDate={nextDateForPicker}
          setSelectedDate={(date) => handleChange({
            target: {
              name: 'next_date',
              value: date,
            },
          })}
        // required
        // hintText="Date the next payment should process."
        />
      )}

      {!isEditPage && (
        <PatternGuardedInputText
          key='transaction_count'
          onChange={handleChange}
          name="transaction_count"
          label="Transaction Count"
          type="text"
          inputMode="numeric"
          maxLength={3}
          value={formData.transaction_count ?? ''}
          required={true}
          hintText='Provide the number of times this recurring payment should be processed. Please enter 999 if the transaction should be processed indefinitely until it is either cancelled or deleted.'
        />
      )}

      {isEditPage && (
        <div className='form-group' style={{ display: 'flex', alignItems: 'center' }}>
          <label style={{ marginRight: '2.9em', marginLeft: '3.6em' }}>Payments Processed</label>
          <div style={{ display: 'inline-flex', border: '1px solid #ccc', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ backgroundColor: '#f0f0f0', padding: '8px 12px', borderRight: '1px solid #ccc' }}>
              {formData.no_of_payment_processed ?? '0'} of
            </div>
                <input
                name="transaction_count"
                value={formData.transaction_count ?? '0'}
                onChange={handleChange}
                style={{
                  border: 'none',
                  borderRight: '1px solid #ccc',
                  outline: 'none',
                  width: '30px',
                  textAlign: 'right',
                }}
              />
          </div>
        </div>
      )}

      <Select
        key='no_of_attempts_if_declined'
        className="small-input"
        onChange={handleChange}
        name="no_of_attempts_if_declined"
        label="# Of Attempts If Declined"
        options={RecurAttemptMap}
        value={formData.no_of_attempts_if_declined}
        hintText='Specify the number of days after a decline that the payment should be retried. Please note that the payment will continue to be processed according to its schedule/count regardless of this setting and ultimate transaction response.'
      />

      <Select
        key='payment_type'
        label="Payment Type"
        options={RecurPaymentTypes}
        name="payment_type"
        value={formData.payment_type}
        onChange={handleChange}
        required
        hintText='Specify if the transaction should be processed on the customers credit card or bank account.'
      />

      {formData.payment_type === RECUR_PAYMENT_VALUE.BANK_ACCOUNT && achMerchantSettings?.processor_code === 'VCI' && (
        <Select
          key='sec_code'
          label="SEC Code"
          options={supportedStdEntryClasses}
          name="sec_code"
          value={formData.sec_code}
          onChange={handleChange}
          required={achMerchantSettings?.processor_code === 'VCI'}
          hintText="Required for ACH transaction."
        />
      )}

      <PatternGuardedInputText
        key='description'
        onChange={handleChange}
        name="description"
        label="Description"
        maxLength={255}
        value={formData.description ?? ''}
        hintText='Provide any additional information about the transaction such as the product/service rendered or other information about the customer.'
      />

      {showSurchargeInfo && <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '2rem', marginLeft: '5rem', marginRight: '10rem' }}>
        {formData.add_surcharge && <span className="req" ><span 
        className="req" 
        style={{marginTop:'-.3em'}}
        >•</span></span>}
        <input
          type="checkbox"
          id="confirm_surcharge_enable"
          name="confirm_surcharge_enable"
          checked={!!formData.confirm_surcharge_enable}
          onChange={handleChange}
          style={{
            marginRight: '0.5em',
            flexShrink: 0,
            marginTop: '0.15em',
          }}
        />
        <label htmlFor="confirm_surcharge_enable">
          {surchargeConfirmText}

        </label>
      </div>}

      <PrimaryButton
        className="primary-btn uid-process form-btn"
        onClick={handleSubmit}
        disabled={submitDisabled || isLoading}
        style={{
          backgroundColor: (submitDisabled || isLoading) ? 'gray' : '#f98e39'
        }}
      >
        Save
      </PrimaryButton>
      {isEditPage && showDeleteButton && <Button
      style={{ backgroundColor: 'white', color: 'black' }}
        onClick={handleDelete}
      >
        <i className="icon-clear-sign"></i>Delete
      </Button>}

    </Fieldset>
    <PrintableConsentForm
      isOpen={isPrintableConsentFormOpen}
      onClose={() => setPrintableConsentFormOpen(false)}
      formData={formData}
      achMerchantSettings={achMerchantSettings}
      customer={customer}
      showSurchargeInfo={showSurchargeInfo}
      customerAchDetails={customerAchDetails}
    />
    </>
  )
}

export default RecurForm
